import React, { useState, useEffect, useRef } from 'react'

export interface EditableCellProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  aircraftId: number
  field: 'owner' | 'broker'
  onBulkEditRequest?: (aircraftId: number, field: 'owner' | 'broker', originalValue: string, newValue: string) => void
}

export function EditableCell({ value, onChange, placeholder, aircraftId, field, onBulkEditRequest }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setEditValue(value)
  }, [value])

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      // Select all text for easy editing
      textareaRef.current.select()

      // Add editing class to parent cell
      const cell = textareaRef.current.closest('.pdf-table-cell')
      if (cell) {
        cell.classList.add('pdf-cell-editing')
      }
    } else {
      // Remove editing class when not editing
      const cells = document.querySelectorAll('.pdf-cell-editing')
      cells.forEach(cell => cell.classList.remove('pdf-cell-editing'))
    }
  }, [isEditing])

  const handleSave = () => {
    if (editValue !== value && onBulkEditRequest) {
      // Check if we should trigger bulk edit confirmation
      onBulkEditRequest(aircraftId, field, value, editValue)
    } else {
      onChange(editValue)
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValue(value)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel()
    } else if (e.key === 'Enter' && e.ctrlKey) {
      // Ctrl+Enter to save
      handleSave()
    }
    // Regular Enter adds line break (default textarea behavior)
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditValue(e.target.value)
  }

  if (isEditing) {
    return (
      <div className="relative w-full" style={{ minHeight: '60px' }}>
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          placeholder={placeholder}
          className="absolute inset-0 w-full resize-none focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white border border-purple-300 z-10"
          style={{
            fontSize: '12px',
            lineHeight: '1.3',
            padding: '3px 6px',
            fontFamily: 'inherit',
            margin: 0,
            minHeight: '60px',
            height: 'auto'
          }}
        />
        <div className="absolute -bottom-4 left-0 text-gray-500 pointer-events-none z-20" style={{ fontSize: '8px' }}>
          Ctrl+Enter to save, Esc to cancel
        </div>
      </div>
    )
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="whitespace-pre-line cursor-pointer hover:bg-black hover:bg-opacity-5 min-h-[1rem]"
      title="Click to edit (Enter for line breaks)"
      style={{ fontSize: '12px', lineHeight: '1.3' }}
    >
      {value || <span className="text-gray-400 italic" style={{ fontSize: '11px' }}>{placeholder}</span>}
    </div>
  )
}

